import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Section, FadeIn } from "@/components/Section";
import {
  CheckCircle2,
  ArrowUpRight,
  ArrowRight,
  CalendarClock,
  LogOut,
  Building2,
  Sparkles,
  Inbox,
} from "lucide-react";
import { Moon, Flame, Dna, FlaskConical, Droplets } from "lucide-react";

const ICONS = {
  sleep: Moon,
  metabolic: Flame,
  hormone: Dna,
  diagnostics: FlaskConical,
  wellness: Droplets,
};

export default function Portal() {
  const { user, logout } = useAuth();
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]); // local stub for now

  useEffect(() => {
    api.get("/programs").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    // local-only demo state
    const stored = JSON.parse(localStorage.getItem("nodera_program_interest") || "[]");
    setRequests(stored);
  }, []);

  const expressInterest = (categoryId, programLabel) => {
    const next = [
      { id: `${categoryId}-${Date.now()}`, category: categoryId, program: programLabel, status: "submitted", at: new Date().toISOString() },
      ...requests,
    ].slice(0, 30);
    setRequests(next);
    localStorage.setItem("nodera_program_interest", JSON.stringify(next));
  };

  return (
    <div data-testid="portal-page">
      <Section className="pt-28 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">Clinic portal</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Welcome, {user?.clinic_name || user?.name || user?.email}.
            </h1>
            <p className="mt-2 text-slate-600 max-w-xl">
              Browse the curated catalog, express interest in programs, and coordinate with our team.
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="portal-logout-btn"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        {/* Profile / approved-access strip */}
        <FadeIn>
          <div data-testid="portal-profile-card" className="mt-8 grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 flex items-start gap-5">
              <span className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg font-semibold text-slate-900">{user?.clinic_name || "Your clinic"}</div>
                  <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.16em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                    <CheckCircle2 className="w-3 h-3" /> Approved
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {user?.email} · Role: <span className="font-semibold text-slate-700">{user?.role}</span>
                </div>
                <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                  Your clinic has full access to the curated catalog and operational layer. Reach out to your concierge for program activation.
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">Concierge</div>
                <p className="mt-2 font-display text-lg font-semibold leading-snug">
                  Need help activating a program?
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  Book a 30-minute call with your operations partner.
                </p>
              </div>
              <a
                href="mailto:info@noderahealth.com?subject=Portal%20concierge%20request"
                data-testid="portal-book-call-btn"
                className="mt-5 inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 self-start"
              >
                <CalendarClock className="w-4 h-4" /> Book a call
              </a>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Program catalog */}
      <Section className="pt-0">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Program catalog</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
              Browse curated programs
            </h2>
          </div>
          <Link to="/programs" className="text-sm font-semibold text-slate-900 hover:text-blue-700 inline-flex items-center gap-1">
            Marketing view <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="space-y-6">
          {categories.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Loading catalog…
            </div>
          )}
          {categories.map((c) => {
            const Icon = ICONS[c.id] || Moon;
            return (
              <div key={c.id} data-testid={`portal-category-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </span>
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold text-slate-900">{c.name}</div>
                    <div className="text-sm text-slate-500">{c.tagline}</div>
                  </div>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  {c.programs.map((p, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-blue-600">Program</div>
                        <div className="mt-1 font-display text-sm font-semibold text-slate-900">{p}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => expressInterest(c.id, p)}
                        data-testid={`portal-interest-${c.id}-${i}`}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-100"
                      >
                        Request <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* My program requests */}
      <Section className="pt-0">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Pipeline</div>
            <h2 className="font-display text-2xl font-bold text-slate-900">My program requests</h2>
          </div>
          <span className="text-xs text-slate-500">Local only · placeholder</span>
        </div>

        {requests.length === 0 ? (
          <div data-testid="portal-requests-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Inbox className="w-6 h-6 text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No requests yet. Click "Request" on any program to track it here.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="portal-requests-list">
            {requests.map((r) => (
              <li key={r.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-display font-semibold text-slate-900 text-sm">{r.program}</div>
                  <div className="text-[11px] text-slate-500 capitalize">{r.category} · {new Date(r.at).toLocaleString()}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.16em] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
                  <Sparkles className="w-3 h-3" /> {r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
